// Purpose: Axios wrapper — baseURL, per-request auth header, AbortController support, 401 handling, Shopee/TikTok token refresh
// Caller: All Redux actions (store/actions/*Action.js), some Views directly
// Dependencies: axios, constant (apiURL)
// Main Functions: xhr.get/post/put/delete/all
// Side Effects: Reads localStorage (accessToken), reload browser on 401, refreshes Shopee/TikTok token and retries request

import axios from "axios";
import { apiURL } from "constant";
import history from "utils/history";

function baseAxios(options) {
  const accessToken = localStorage.getItem("accessToken");

  const headers = {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    "max-age": 0,
    ...(accessToken ? { Authorization: accessToken } : {}),
    ...options.headers,
  };

  return axios.create({
    baseURL: `${apiURL}`,
    timeout: options.timeout || 30000,
    headers,
  });
}

async function executeRequest(method, pathname, data, options = {}) {
  const body = method === "get" || !data ? {} : { data };
  const reqObj = {
    url: pathname,
    method,
    params: options.query,
    ...body,
  };

  if (options.signal) {
    if (options.signal.aborted) {
      throw new DOMException("Request cancelled", "AbortError");
    }
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    reqObj.cancelToken = source.token;
    options.signal.addEventListener(
      "abort",
      () => source.cancel("Request cancelled"),
      { once: true },
    );
  }

  try {
    const baseAxiosRequest = baseAxios(options);
    const res = await baseAxiosRequest.request(reqObj);

    return res.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw new DOMException("Request cancelled", "AbortError");
    }

    const res = error.response;

    if (res && res.status && res.status === 401 && pathname !== '/login') {
      localStorage.setItem("accessToken", "");
      history.push('/login');
      throw new Error("Sesi berakhir, silakan login ulang");
    }

    if (!res) {
      throw new Error("Koneksi bermasalah, silakan coba lagi");
    }

    const errMsg =
      res && res.data && res.data.message ? res.data.message : error.message;

    const shouldRefreshShopee =
      !options._isRetry &&
      ["error_auth", "invalid_acceess_token"].includes(errMsg);
    const shouldRefreshTiktok =
      !options._isRetry && String(errMsg) === "105002";

    if (shouldRefreshShopee) {
      try {
        const baseAxiosRequest = baseAxios(options);
        await baseAxiosRequest.post(`${apiURL}/shopee/refresh-token`);
        return executeRequest(method, pathname, data, {
          ...options,
          _isRetry: true,
        });
      } catch (refreshErr) {
        if (axios.isCancel(refreshErr)) {
          throw new DOMException("Request cancelled", "AbortError");
        }
      }
    }

    if (shouldRefreshTiktok) {
      try {
        const baseAxiosRequest = baseAxios(options);
        await baseAxiosRequest.post(`${apiURL}/tiktok/refresh-token`);
        return executeRequest(method, pathname, data, {
          ...options,
          _isRetry: true,
        });
      } catch (refreshErr) {
        if (axios.isCancel(refreshErr)) {
          throw new DOMException("Request cancelled", "AbortError");
        }
      }
    }

    throw new Error(errMsg);
  }
}

export const xhr = {
  async get(pathname, options) {
    return executeRequest("get", pathname, null, options);
  },

  async post(pathname, data, options) {
    return executeRequest("post", pathname, data, options);
  },

  async put(pathname, data, options) {
    return executeRequest("put", pathname, data, options);
  },

  async delete(pathname, data, options) {
    return executeRequest("delete", pathname, data, options);
  },

  all(promises) {
    return Promise.all(promises);
  },
};
